// RateChangeModal — the ONE place a rate change is made.
//
// It used to be "Propose change": you typed a figure, somebody else approved it,
// and only then did the money move. Ben ended that on 2026-09-08 — "When
// changed, the change takes effect. Finance, National AM, and execs are
// notified. Stop making additional processes." In thirteen months that approval
// step had never once said no.
//
// ⛔ Every screen that can start a rate change opens THIS, and this calls the
// one function that writes a rate (TCSD-0188). Nothing else writes the column.
//
// The date is the whole point of the change. Today or any past date takes
// effect on save. A future date is booked and lands on its own night, and
// anyone can call it off before it does — a restriction on who may cancel is
// not the control; the record of who did is (TCSD-0187 §3).
// ============================================================================

import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../shared/components';
import { useRateChange } from '../hooks/use-rate-change';
import { overlayClickClose } from '../../../shared/overlay-close';
import { perHour } from '../../../shared/rate-basis';

export interface RateChangeModalProps {
  open: boolean;
  onClose: () => void;
  /** The LINE: a service line (ASN-…) or a client's billing line (FBL-…,
   *  item 867). ⛔ Never the building — the facility rate is the sum of its
   *  lines and nobody types it (TCSD-0124). */
  assignment_id: string;
  /** sub_rate is a billing line's sub rate; sub_rate_monthly a service line's. */
  field_name: 'sub_rate_monthly' | 'billing_rate' | 'sub_rate' | 'sub_hourly_rate';
  field_label: string;
  current_value: number | null;
  /**
   * Items 831/758: the figure is per hour (an hourly line's client rate, or
   * its sub hourly rate). The window says "an hour" and asks for an hourly figure.
   */
  per_hour?: boolean;
  /** Called after the change is recorded, so the caller can refresh. */
  onRecorded?: (change_id: string) => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function RateChangeModal({
  open,
  onClose,
  assignment_id,
  field_name,
  field_label,
  current_value,
  per_hour = false,
  onRecorded,
}: RateChangeModalProps) {
  const { record, employeeId, employeeError } = useRateChange();
  const [proposed, setProposed] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [effective, setEffective] = useState<string>(todayIso());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; status: string; on: string } | null>(null);

  if (!open) return null;

  const isFuture = effective > todayIso();
  const isPast = effective < todayIso();

  async function handleSubmit() {
    const num = Number(proposed);
    if (!proposed || Number.isNaN(num)) {
      setError('Enter the new figure.');
      return;
    }
    if (!reason.trim()) {
      setError('Say why the rate is changing.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await record({
      assignment_id,
      field_name,
      new_value: num,
      reason,
      effective_date: effective || null,
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'The change could not be recorded.');
      return;
    }
    setDone({
      id: result.change_id ?? '',
      status: result.status ?? 'Applied',
      on: result.effective_date ?? effective,
    });
    if (result.change_id && onRecorded) onRecorded(result.change_id);
  }

  function handleClose() {
    setProposed('');
    setReason('');
    setEffective(todayIso());
    setError(null);
    setDone(null);
    onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(handleClose)}
    >
      <div
        className="w-full max-w-md rounded-xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-tcs-text">
              Change the {field_label.toLowerCase()}
            </h3>
            <p className="mt-1 text-xs text-tcs-text-muted">
              It takes effect on the date you give. Finance, the National Account Manager
              and the executives are told.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="mt-4 space-y-3">
            <p className="rounded bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg-strong">
              {done.status === 'Scheduled'
                ? `Booked for ${done.on}. It switches over on its own — you do not need to come back to it, and anyone can call it off before then.`
                : `Done — the rate changed as of ${done.on}, and the people who need to know have been told.`}
            </p>
            <Button variant="primary" size="sm" onClick={handleClose}>
              Done
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                Now
              </label>
              <p className="mt-1 text-sm text-tcs-text">
                {current_value != null
                  ? per_hour
                    ? perHour(current_value)
                    : `$${current_value.toFixed(2)}`
                  : '— (not set)'}
              </p>
            </div>

            <div>
              <label
                htmlFor="rc-new"
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted"
              >
                {per_hour ? 'New figure ($ an hour)' : 'New figure'}
              </label>
              <input
                id="rc-new"
                type="number"
                step="0.01"
                min="0"
                value={proposed}
                onChange={(e) => setProposed(e.target.value)}
                placeholder="0.00"
                className="mt-1 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="rc-effective"
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted"
              >
                Takes effect
              </label>
              <input
                id="rc-effective"
                type="date"
                value={effective}
                onChange={(e) => setEffective(e.target.value)}
                className="mt-1 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              />
              {/* ⛔ One field, no mode. A past date is never refused and never
                  warned as late — most changes are recorded after the fact. The
                  line underneath just says what that date means. */}
              <p className="mt-1 text-xs text-tcs-text-muted">
                {isFuture
                  ? 'Booked for later. It switches over on its own that night.'
                  : isPast
                    ? 'Already happened. It takes effect now and is recorded as of that date.'
                    : 'Takes effect now.'}
              </p>
            </div>

            <div>
              <label
                htmlFor="rc-reason"
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted"
              >
                Why
              </label>
              <textarea
                id="rc-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="What changed? Added scope, renegotiated with the sub, annual increase…"
                className="mt-1 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              />
            </div>

            {error && (
              <p className="rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong">{error}</p>
            )}
            {employeeError && (
              // Plain English. The old copy here once read "Employee lookup
              // error: JSON object requested, multiple (or no) rows returned" —
              // a Postgrest sentence shown to a COO, describing our own bug.
              <p className="rounded bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong">
                {employeeError}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="secondary" size="sm" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting || !employeeId}
              >
                {submitting ? 'Saving…' : isFuture ? 'Book the change' : 'Change the rate'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
